import { useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import {
  Plus, X, Loader2, Sparkles, TrendingUp, TrendingDown,
  CheckCircle2, AlertTriangle, XCircle,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import ArtifactQualityReview from "../../ArtifactQualityReview";
import CreditCostTag from "../../CreditCostTag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_ICONS = {
  on_track: { icon: CheckCircle2, color: "text-[hsl(var(--success))]" },
  at_risk: { icon: AlertTriangle, color: "text-[hsl(var(--warning))]" },
  off_track: { icon: XCircle, color: "text-destructive" },
};

export default function KpiDashboardTab({ projectId, token, kpis, setKpis }) {
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [selectedKPI, setSelectedKPI] = useState(null);
  const [kpiHistory, setKpiHistory] = useState([]);

  const add = () => {
    setKpis(prev => [...prev, { kpi_id: "", name: "", target: "", unit: "", frequency: "weekly", current_value: "", upper_threshold: "", lower_threshold: "", owner: "", status: "on_track" }]);
  };

  const update = (index, field, value) => {
    setKpis(prev => prev.map((k, i) => i === index ? { ...k, [field]: value } : k));
  };

  const remove = (index) => {
    if (kpis.length <= 1) return;
    setKpis(prev => prev.filter((_, i) => i !== index));
  };

  const aiSuggest = async () => {
    setSuggesting(true);
    try {
      const response = await fetch(`${API}/ai/suggest-kpis`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: projectId }),
      });
      if (!response.ok) throw new Error("Failed to generate KPIs");
      const data = await response.json();
      if (data.kpis?.length) {
        setKpis(data.kpis.map(k => ({
          kpi_id: "", name: k.name || "", target: k.target ?? "", unit: k.unit || "",
          frequency: k.frequency || "weekly", current_value: "",
          upper_threshold: k.upper_threshold ?? "", lower_threshold: k.lower_threshold ?? "",
          owner: k.owner || "", status: "on_track",
        })));
        toast.success(`AI generated ${data.kpis.length} KPIs`);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSuggesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const kpiData = kpis.filter(k => k.name.trim()).map(k => ({
        ...k,
        target: parseFloat(k.target) || 0,
        current_value: k.current_value ? parseFloat(k.current_value) : null,
        upper_threshold: k.upper_threshold ? parseFloat(k.upper_threshold) : null,
        lower_threshold: k.lower_threshold ? parseFloat(k.lower_threshold) : null,
      }));
      const response = await fetch(`${API}/projects/${projectId}/control/kpis`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ kpis: kpiData }),
      });
      if (!response.ok) throw new Error("Failed to save");
      const data = await response.json();
      if (data.kpis) setKpis(data.kpis);
      toast.success("KPIs saved");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const fetchHistory = async (kpiId) => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/control/kpis/${kpiId}/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setKpiHistory(data.history || data || []);
        setSelectedKPI(kpis.find(k => k.kpi_id === kpiId));
      }
    } catch (error) {
      console.error("Failed to fetch KPI history:", error);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold">KPI Dashboard</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={aiSuggest} disabled={suggesting} className="gap-1" data-testid="ai-suggest-kpis-btn">
            {suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {suggesting ? "Generating..." : "AI Suggest"}
            <CreditCostTag cost={1} />
          </Button>
          <Button variant="outline" size="sm" onClick={add} data-testid="add-kpi-btn">
            <Plus className="w-4 h-4 mr-1" /> Add KPI
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {kpis.filter(k => k.name && k.kpi_id).map((kpi, index) => {
          const StatusIcon = STATUS_ICONS[kpi.status]?.icon || CheckCircle2;
          const statusColor = STATUS_ICONS[kpi.status]?.color || "text-muted-foreground";
          const isOnTarget = kpi.current_value && kpi.target && Math.abs(kpi.current_value - kpi.target) / kpi.target < 0.1;
          return (
            <div key={kpi.kpi_id} className="bg-background border border-border rounded-lg p-4 cursor-pointer hover:border-primary/50 transition-colors" onClick={() => fetchHistory(kpi.kpi_id)} data-testid={`kpi-card-${index}`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-sm">{kpi.name}</h4>
                <StatusIcon className={`w-5 h-5 ${statusColor}`} />
              </div>
              <div className="flex items-end gap-2">
                <span className="text-2xl font-bold font-heading">{kpi.current_value ?? "\u2014"}</span>
                <span className="text-sm text-muted-foreground mb-1">{kpi.unit}</span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <span>Target: {kpi.target} {kpi.unit}</span>
                {isOnTarget ? <TrendingUp className="w-3 h-3 text-[hsl(var(--success))]" /> : <TrendingDown className="w-3 h-3 text-destructive" />}
              </div>
            </div>
          );
        })}
      </div>

      {/* KPI History Chart */}
      {selectedKPI && kpiHistory.length > 0 && (
        <div className="mb-6 p-4 bg-background border border-border rounded-lg">
          <h4 className="font-medium mb-4">{selectedKPI.name} - Trend</h4>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={kpiHistory.map((h, i) => ({ index: i + 1, value: h.value, date: h.recorded_at }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2F3A" />
                <XAxis dataKey="index" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip contentStyle={{ backgroundColor: "#1A1D23", border: "1px solid #2A2F3A" }} />
                {selectedKPI.target && <ReferenceLine y={selectedKPI.target} stroke="#2E66FF" strokeDasharray="5 5" label="Target" />}
                <Line type="monotone" dataKey="value" stroke="#1FBF8F" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* KPI Configuration */}
      <div className="space-y-4">
        <h4 className="font-medium">KPI Configuration</h4>
        {kpis.map((kpi, index) => (
          <div key={index} className="p-4 bg-background border border-border rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div className="md:col-span-2 space-y-1">
                <Label className="text-xs">KPI Name</Label>
                <Input value={kpi.name} onChange={(e) => update(index, "name", e.target.value)} placeholder="e.g., Defect Rate" data-testid={`kpi-name-${index}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Target</Label>
                <Input type="number" value={kpi.target} onChange={(e) => update(index, "target", e.target.value)} placeholder="100" data-testid={`kpi-target-${index}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Unit</Label>
                <Input value={kpi.unit} onChange={(e) => update(index, "unit", e.target.value)} placeholder="%" data-testid={`kpi-unit-${index}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Frequency</Label>
                <select value={kpi.frequency} onChange={(e) => update(index, "frequency", e.target.value)} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid={`kpi-freq-${index}`}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div className="flex items-end">
                {kpis.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => remove(index)} className="text-destructive"><X className="w-4 h-4" /></Button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-3">
              <div className="space-y-1">
                <Label className="text-xs">Current Value</Label>
                <Input type="number" value={kpi.current_value} onChange={(e) => update(index, "current_value", e.target.value)} placeholder="Current" data-testid={`kpi-current-${index}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Upper Threshold</Label>
                <Input type="number" value={kpi.upper_threshold} onChange={(e) => update(index, "upper_threshold", e.target.value)} placeholder="Alert if above" data-testid={`kpi-upper-${index}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Lower Threshold</Label>
                <Input type="number" value={kpi.lower_threshold} onChange={(e) => update(index, "lower_threshold", e.target.value)} placeholder="Alert if below" data-testid={`kpi-lower-${index}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Owner</Label>
                <Input value={kpi.owner} onChange={(e) => update(index, "owner", e.target.value)} placeholder="Responsible person" data-testid={`kpi-owner-${index}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button onClick={save} disabled={saving} className="mt-4" data-testid="save-kpis-btn">
        {saving ? "Saving..." : "Save KPIs"}
      </Button>

      {kpis.some(k => k.name && k.kpi_id) && (
        <ArtifactQualityReview projectId={projectId} artifactType="kpi_dashboard" token={token} className="mt-6" />
      )}
    </div>
  );
}
